import axios from 'axios';
import { displayErrorMessage } from 'Helpers/FacetNotificationManager';

const KB = 1000;
const MB = 1000 * KB;
const GB = 1000 * MB;

const validFileName = (filename) => {
  const re = /^[a-zA-Z0-9- _.()]+$/;
  return re.test(filename);
};

const validFolderName = foldername => validFileName(foldername);

const stripInvalidCharacters = (filename) => {
  const re = /[^\w-.]/gi;
  return filename.replace(re, '');
};

const fileIcon = (filename) => {
  if (filename.endsWith('.pdf')) {
    return 'file-pdf-o';
  }
  if (filename.endsWith('.zip')) {
    return 'file-archive-o';
  }
  if (filename.endsWith('.xsl') || filename.endsWith('.xslx')) {
    return 'file-excel-o';
  }
  if (filename.endsWith('.doc') || filename.endsWith('.docx')) {
    return 'file-word-o';
  }
  if (
    filename.endsWith('.png')
    || filename.endsWith('.jpg')
    || filename.endsWith('.jpeg')
  ) {
    return 'file-image-o';
  }
  return 'file-o';
};

const getMaxFileSizeExceededString = (maxAllowed) => {
  let fileSizeStr;

  if (maxAllowed < KB) {
    fileSizeStr = `${maxAllowed} Bytes`;
  } else if (maxAllowed < MB) {
    fileSizeStr = `${maxAllowed / KB} KB`;
  } else if (maxAllowed < GB) {
    fileSizeStr = `${maxAllowed / MB} MB`;
  } else {
    fileSizeStr = `${maxAllowed / MB} MB`;
  }

  return `Max file size of ${fileSizeStr} exceeded`;
};

const uploadFileRaw = (
  urlString,
  files,
  params,
  setProgressCallback,
  method = 'post',
  additionalFormData = {},
) => {
  if (files.length === 0 || (files.length === 1 && files[0].size === 0)) {
    // There are no files or the only file is empty, so we don't need to upload anything.'
    return null;
  }
  const url = new URL(urlString);
  const formData = new FormData();

  files.forEach((file) => {
    formData.append('file', file);
  });

  if (additionalFormData) {
    Object.keys(additionalFormData).forEach((objectKey) => {
      formData.append(String(objectKey), JSON.stringify(additionalFormData[objectKey]));
    });
  }

  if (params) {
    url.search = new URLSearchParams(params).toString();
  }

  const opts = {
    credentials: 'include',
    method,
    body: formData,
  };

  return axios({
    url: url.toString(),
    method: opts.method,
    data: opts.body,
    withCredentials: true,
    onUploadProgress: (progress) => {
      const { loaded, total } = progress;
      const percentageProgress = Math.floor((loaded / total) * 100);
      if (setProgressCallback) setProgressCallback(percentageProgress);
    },
  });
};

const getUploadText = (progress) => {
  switch (progress) {
    case 0:
      return 'Preparing...';
    case 100:
      return 'Processing...';
    default:
      return 'Uploading...';
  }
};

function forceDownload(blob, filename) {
  const a = document.createElement('a');
  a.download = filename;
  a.href = blob;
  // For Firefox https://stackoverflow.com/a/32226068
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export const downloadSecureFileFromLink = async (downloadLink, fileName, onFailure) => {
  try {
    const response = await downloadLink.get({ raw: true });
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    forceDownload(blobUrl, fileName);
  } catch (err) {
    if (onFailure) {
      onFailure();
    } else {
      displayErrorMessage(err, 'Error downloading file');
    }
  }
};

export {
  fileIcon,
  validFileName,
  validFolderName,
  stripInvalidCharacters,
  getMaxFileSizeExceededString,
  uploadFileRaw,
  getUploadText,
};
